import React from 'react';
import { biography, horses } from '../data/mock';
import { Trophy, Award, Target, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Biography = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_b6c20734-bb90-4750-94f8-baaefceffec0/artifacts/l6mqx7qg_7122CC28-0874-44F9-A240-349F48E17EB0_1_201_a.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        {/* Vignette Effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,0.95) 100%)'
          }}
        ></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">{biography.title}</h1>
          <p className="text-xl md:text-2xl font-light">{t('fromZeroToHero')}</p>
        </div>
      </section>

      {/* Biography Content */}
      <section className="py-20 bg-neutral-950">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            {biography.bio.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-300 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">{t('careerAchievements')}</h2>
            <p className="text-gray-400">{t('highlightsFromCompetitions')}</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="grid gap-4">
              {biography.achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="bg-neutral-900 border border-neutral-800 hover:border-cyan-400/50 p-6 rounded-lg hover:shadow-lg transition-all duration-300 flex items-start space-x-4"
                >
                  <Trophy className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 font-medium">{achievement}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-neutral-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">{t('myValues')}</h2>
            <p className="text-gray-400">{t('whatDrivesMe')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-neutral-900 border border-neutral-800 rounded-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-400/10 rounded-full mb-4">
                <Target className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">{t('excellence')}</h3>
              <p className="text-gray-400">
                {t('excellenceDesc')}
              </p>
            </div>
            <div className="text-center p-8 bg-neutral-900 border border-neutral-800 rounded-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-400/10 rounded-full mb-4">
                <Heart className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">{t('partnership')}</h3>
              <p className="text-gray-400">
                {t('partnershipDesc')}
              </p>
            </div>
            <div className="text-center p-8 bg-neutral-900 border border-neutral-800 rounded-lg">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-400/10 rounded-full mb-4">
                <Award className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">{t('pride')}</h3>
              <p className="text-gray-400">
                {t('prideDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-black border-t border-cyan-400/10 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-5xl font-bold mb-2 text-cyan-400" data-testid="bio-stat-fei-starts">{biography.feiStats.starts.toLocaleString()}</p>
              <p className="text-gray-400 uppercase tracking-wide text-sm">{t('feiStarts')}</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2 text-cyan-400" data-testid="bio-stat-ranking">{biography.worldRanking.position}</p>
              <p className="text-gray-400 uppercase tracking-wide text-sm">{t('worldRanking').split(' ')[0]} Ranking</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2 text-cyan-400" data-testid="bio-stat-fei-wins">{biography.feiStats.wins}</p>
              <p className="text-gray-400 uppercase tracking-wide text-sm">{t('internationalWins')}</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2 text-cyan-400" data-testid="bio-stat-horses">{horses.length}</p>
              <p className="text-gray-400 uppercase tracking-wide text-sm">{t('competitionHorsesCount')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Biography;